import React, { FC, useEffect, useState } from 'react';
import 'reactjs-popup/dist/index.css';
import { GrayButton } from '@shared/uikit/button';
import IconCross from '@shared/icons/IconCross';
import SingleSelect from '@widgets/single-select';
import { ISelectOption } from '@widgets/multi-select';
import { useManagerOptions } from '@app/queries/options/useManagerOptions';
import { useRegionOptions } from '@app/queries/options/useRegionOptions';
import { useSourceOptions } from '@app/queries/options/useSourceOptions';
import { useTagOptions } from '@app/queries/options/useTagOptions';
import { TextInput } from '@shared/uikit/input-text';
import { ValidationError } from '@app/error/ValidationError';
import { ServerError } from '@app/error/ServerError';
import { Loader } from '@shared/uikit/loader';
import Label from '@shared/uikit/label';
import { TextInputWithSign } from '@shared/uikit/input-text-with-sign';
import { Popup } from '@shared/uikit/popup';

interface IProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    managerId: ISelectOption | null,
    regionId: ISelectOption | null,
    tagId: ISelectOption | null,
    sourceId: ISelectOption | null,
    clientName: string | null,
    clientPercent: string | null
  ) => void;
}

export const CreateClientPopup: FC<IProps> = ({ open, onClose, onSubmit }) => {
  const managerOptionsFetcher = useManagerOptions();
  const regionOptionsFetcher = useRegionOptions();
  const sourceOptionsFetcher = useSourceOptions();
  const tagOptionsFetcher = useTagOptions();

  const [managerId, setManagerId] = useState<ISelectOption | null>(null);
  const [regionId, setRegionId] = useState<ISelectOption | null>(null);
  const [tagId, setTagId] = useState<ISelectOption | null>(null);
  const [sourceId, setSourceId] = useState<ISelectOption | null>(null);
  const [clientName, setClientName] = useState<string>('');
  const [clientPercent, setClientPercent] = useState<string>('');

  const [submitting, setSubmitting] = useState<boolean>(false);

  const [invalid, setInvalid] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const [managerOptions, setManagerOptions] = useState<ISelectOption[]>();
  const [regionOptions, setRegionOptions] = useState<ISelectOption[]>();
  const [sourceOptions, setSourceOptions] = useState<ISelectOption[]>();
  const [tagOptions, setTagOptions] = useState<ISelectOption[]>();

  useEffect(() => {
    (async () => {
      if (!open) return;

      const data = await managerOptionsFetcher.fetchData();
      setManagerOptions(data);
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (!open) return;

      const data = await regionOptionsFetcher.fetchData();
      setRegionOptions(data);
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (!open) return;

      const data = await sourceOptionsFetcher.fetchData();
      setSourceOptions(data);
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (!open) return;

      const data = await tagOptionsFetcher.fetchData();
      setTagOptions(data);
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (open) return;

      onClear();
    })();
  }, [open]);

  const onClear = () => {
    setManagerId(null);
    setRegionId(null);
    setTagId(null);
    setSourceId(null);
    setInvalid(false);
    setClientName('');
    setClientPercent('');
    setError('');
    setSubmitting(false);
  };

  return (
    <Popup open={open} width="500px" onClose={onClose}>
      <div className="border-b border-solid pb-[15px] border-opacity-10 border-[#959595] relative">
        <input type="hidden" />
        <div className="flex items-center">
          <TextInputWithSign
            value={clientName}
            onChange={value => setClientName(value)}
            placeholder="Введите имя клиента"
            width="210px"
            bgColor="#211F24"
          />
        </div>
        <div className="absolute right-0 top-[10px] cursor-pointer" onClick={onClose}>
          <IconCross />
        </div>
      </div>

      <div className="grid grid-cols-[1fr_1fr] gap-3 justify-items-start items-center py-[20px] border-b border-solid border-opacity-10 border-[#959595]">
        <Label title="Менеджер" color="white" />
        <SingleSelect
          title="Менеджер"
          isLoading={!managerOptionsFetcher.isSuccess}
          options={managerOptions}
          width="100%"
          onChange={value => setManagerId(value)}
          value={managerId}
        />
        <Label title="Регион" color="white" />
        <SingleSelect
          title="Регион"
          isLoading={!regionOptionsFetcher.isSuccess}
          options={regionOptions}
          width="100%"
          onChange={value => setRegionId(value)}
          value={regionId}
        />
        <Label title="Источник" color="white" />
        <SingleSelect
          options={sourceOptions}
          title="Источник"
          isLoading={!sourceOptionsFetcher.isSuccess}
          width="100%"
          onChange={value => setSourceId(value)}
          value={sourceId}
          isCreatable={true}
        />
        <Label title="Тег" color="white" />
        <SingleSelect
          isLoading={!tagOptionsFetcher.isSuccess}
          options={tagOptions}
          title="Тег"
          width="100%"
          onChange={value => setTagId(value)}
          value={tagId}
          isCreatable={true}
        />
        <Label title="Условия на ViaBTC, %" color="white" />
        <div className="w-full flex items-center gap-2 text-white">
          <div className={'ml-auto'}>
            <TextInput
              width="60px"
              value={clientPercent}
              onChange={value => setClientPercent(value)}
              placeholder=""
            />
          </div>
          %
        </div>
      </div>
      <div className="text-left mt-[15px] flex items-center justify-between">
        <div className="cursor-pointer text-[10px]" onClick={onClear}>
          Сбросить данные
        </div>
        <div className="text-[white] flex gap-[10px]">
          {!submitting &&
          invalid &&
          (error || !managerId || !regionId || !sourceId || !clientName || !clientPercent) ? (
            <div className="flex gap-[5px] items-center w-[200px] text-[12px] overflow-hidden bg-[#201818] rounded-[8px] px-[15px] text-[#A93434]">
              <div className="12px pr-[5px]">
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 11 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="5.5" cy="5.5" r="5.5" fill="#A93434" />
                  <path d="M3 3L5.5 5.5M5.5 5.5L8 3M5.5 5.5L3 8M5.5 5.5L8 8" stroke="black" />
                </svg>
              </div>
              {error}
            </div>
          ) : null}

          {!submitting ? (
            <GrayButton
              onClick={async () => {
                try {
                  setSubmitting(true);
                  await onSubmit(managerId, regionId, tagId, sourceId, clientName, clientPercent);
                } catch (e) {
                  setSubmitting(false);
                  if (e instanceof ValidationError) {
                    setInvalid(true);
                    setError('Введите имя клиента');
                  }
                  if (e instanceof ServerError) {
                    setInvalid(true);
                    setError(e.error);
                  }
                }
              }}
              title="Создать клиента"
            />
          ) : (
            <Loader size={30} loading={true} />
          )}
        </div>
      </div>
    </Popup>
  );
};
